import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiLock, FiEye, FiEyeOff } from 'react-icons/fi';

const LoginContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, var(--primary-blue) 0%, #1E40AF 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const LoginCard = styled(motion.div)`
  background: white;
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
`;

const LoginTitle = styled.h2`
  text-align: center;
  color: var(--primary-blue);
  margin-bottom: 8px;
  font-size: 2rem;
  font-weight: 700;
`;

const LoginSubtitle = styled.p`
  text-align: center;
  color: var(--medium-gray);
  margin-bottom: 32px;
`;

const InputGroup = styled.div`
  position: relative;
  margin-bottom: 24px;
`;

const InputLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  color: var(--primary-blue);
  font-weight: 600;
`;

const PasswordInput = styled.input`
  width: 100%;
  padding: 12px 40px 12px 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-orange);
  }
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--medium-gray);
  cursor: pointer;
  padding: 4px;
`;

const LoginButton = styled(motion.button)`
  width: 100%;
  background: var(--primary-orange);
  color: white;
  border: none;
  padding: 14px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 0.9rem;
`;

const BackLink = styled.a`
  display: block;
  text-align: center;
  color: var(--primary-blue);
  text-decoration: none;
  margin-top: 24px;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Simple password for now - in production, this would be handled by proper auth
  const ADMIN_PASSWORD = 'ahaar2024sewa';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Simulate API call delay
    setTimeout(() => {
      if (password === ADMIN_PASSWORD) {
        localStorage.setItem('ahaarsewa_admin_auth', 'true');
        onLogin();
      } else {
        setError('Incorrect password. Please try again.');
      }
      setIsLoading(false);
    }, 500);
  };

  return (
    <LoginContainer>
      <LoginCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <LoginTitle>Admin Access</LoginTitle>
        <LoginSubtitle>Ahaar Sewa Event Management</LoginSubtitle>

        <form onSubmit={handleSubmit}>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          <InputGroup>
            <InputLabel>Password</InputLabel>
            <div style={{ position: 'relative' }}>
              <PasswordInput
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                required
              />
              <ToggleButton
                type="button"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </ToggleButton>
            </div>
          </InputGroup>

          <LoginButton
            type="submit"
            disabled={isLoading || !password}
            whileHover={{ scale: isLoading ? 1 : 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FiLock />
            {isLoading ? 'Authenticating...' : 'Access Admin Panel'}
          </LoginButton>
        </form>

        <BackLink href="/">← Back to Main Site</BackLink>
      </LoginCard>
    </LoginContainer>
  );
};

export default AdminLogin; 